import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, Check, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_KEY = "pb_saved_email";
const SUBSCRIBED_KEY = "pb_journal_subscribed";

/**
 * Journal newsletter capture. Two visual layouts:
 *   variant="card"   → full gradient capstone card (used at bottom of /journal)
 *   variant="inline" → compact horizontal band (used inside each post)
 *
 * De-dupes locally with `localStorage.pb_journal_subscribed` so a returning
 * reader doesn't see the ask twice.
 */
export default function JournalNewsletter({ variant = "card", className = "" }) {
  const [email, setEmail] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(EMAIL_KEY) || "";
  });
  const [sending, setSending] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setSubscribed(window.localStorage.getItem(SUBSCRIBED_KEY) === "1");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = email.trim().toLowerCase();
    if (!EMAIL_RE.test(cleaned)) {
      toast.error("Hmm, that email looks off. Try again?");
      return;
    }
    setSending(true);
    try {
      await api.post("/email-signup", { email: cleaned, source: "journal_newsletter" });
      window.localStorage.setItem(EMAIL_KEY, cleaned);
      window.localStorage.setItem(SUBSCRIBED_KEY, "1");
      setSubscribed(true);
      trackEvent("newsletter_signup", { source: "journal" });
      toast.success("You're in! Check your inbox for a welcome from us.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't save that — try again in a sec?",
      );
    } finally {
      setSending(false);
    }
  };

  // --- INLINE variant (used inside posts) ---
  if (variant === "inline") {
    if (subscribed) {
      return (
        <aside
          data-testid="newsletter-inline-subscribed"
          className={`mt-10 rounded-2xl bg-pb-cream border-l-4 border-pb-green p-5 flex items-center gap-3 ${className}`}
        >
          <div className="h-9 w-9 rounded-full bg-pb-green/20 flex items-center justify-center shrink-0">
            <Check className="h-4 w-4 text-pb-green" strokeWidth={3} />
          </div>
          <div>
            <p className="text-sm font-black text-pb-ink">You're on the list</p>
            <p className="text-xs text-pb-soft">Watch for weekly dinner ideas in your inbox.</p>
          </div>
        </aside>
      );
    }
    return (
      <aside
        data-testid="newsletter-inline"
        className={`mt-10 rounded-2xl bg-pb-cream border-l-4 border-pb-coral p-5 sm:p-6 ${className}`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-[11px] font-black uppercase tracking-widest text-pb-coral inline-flex items-center gap-1.5">
              <Mail className="h-3 w-3" /> The Sunday Plate
            </p>
            <p className="font-display font-black text-lg text-pb-ink mt-1 leading-tight">
              Get one dinner idea every Sunday.
            </p>
            <p className="text-xs text-pb-soft mt-1">
              Real recipes, real playbooks, zero spam. Unsubscribe anytime.
            </p>
          </div>
          <form onSubmit={submit} className="flex-1 flex flex-col sm:flex-row gap-2 min-w-0">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-inline-email"
              className="flex-1 min-w-0 px-4 py-2.5 rounded-full border-2 border-white bg-white text-sm font-semibold text-pb-ink placeholder:text-pb-soft/60 focus:border-pb-coral outline-none"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="newsletter-inline-submit"
              className="px-5 py-2.5 rounded-full bg-pb-ink text-white text-xs font-black uppercase tracking-wider whitespace-nowrap hover:bg-pb-coral transition disabled:opacity-60 inline-flex items-center gap-1.5 justify-center"
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Sign me up"}
            </button>
          </form>
        </div>
      </aside>
    );
  }

  // --- CARD variant (capstone) ---
  if (subscribed) {
    return (
      <div
        data-testid="newsletter-card-subscribed"
        className={`rounded-[2.5rem] bg-white border-2 border-dashed border-pb-green/50 p-8 md:p-12 text-center ${className}`}
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-pb-green/20 flex items-center justify-center">
          <Check className="h-7 w-7 text-pb-green" strokeWidth={3} />
        </div>
        <h3 className="font-display font-black text-2xl md:text-3xl mt-4 text-pb-ink">
          You&rsquo;re on the list.
        </h3>
        <p className="mt-2 text-pb-soft max-w-md mx-auto">
          One dinner idea every Sunday. First email lands this weekend. Talk soon.
        </p>
      </div>
    );
  }
  return (
    <div
      data-testid="newsletter-card"
      className={`rounded-[2.5rem] bg-gradient-to-br from-pb-sun/70 via-white to-pb-sky/50 p-8 md:p-12 border border-white shadow-[0_20px_50px_rgba(28,22,40,0.08)] ${className}`}
    >
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-[11px] font-black uppercase tracking-widest text-pb-coral inline-flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> The Sunday Plate · Weekly Newsletter
        </p>
        <h3 className="font-display font-black text-3xl md:text-4xl mt-3 leading-tight text-pb-ink">
          One dinner idea. Every Sunday. Never bribes.
        </h3>
        <p className="mt-3 text-pb-soft leading-relaxed">
          Join <b>4,000+ parents</b> who get one tested, kid-friendly recipe + one screen-free ritual delivered Sunday morning — perfect for the week's grocery run.
        </p>
        <form
          onSubmit={submit}
          className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
        >
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="newsletter-card-email"
            className="flex-1 min-w-0 px-5 py-3.5 rounded-full border-2 border-white bg-white text-sm font-semibold text-pb-ink placeholder:text-pb-soft/60 focus:border-pb-coral outline-none shadow-sm"
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="newsletter-card-submit"
            className="px-6 py-3.5 rounded-full bg-pb-coral text-white text-sm font-black uppercase tracking-wider hover:bg-pb-ink transition disabled:opacity-60 inline-flex items-center gap-2 justify-center shadow-lg"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4" /> Sign me up
              </>
            )}
          </button>
        </form>
        <p className="mt-3 text-[11px] text-pb-soft">
          Unsubscribe with one click. No spam, no data resale, ever.
        </p>
      </div>
    </div>
  );
}
